import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { MedicationOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getPatientMedications } from '../../../../services/medication';
import OverviewCard from './OverviewCard';

const formatTakingMoments = (dosage, t, language) => {
    if (!Array.isArray(dosage) || dosage.length === 0) {
        return t('userPage.overview.medications.noSchedule');
    }

    const moments = [
        ...new Set(
            dosage.map(([period]) =>
                t(`userPage.overview.medications.takingMoments.${period}`, {
                    defaultValue: period
                })
            )
        )
    ];

    const locale = language === 'en' ? 'en' : 'pt';
    const formattedMoments = new Intl.ListFormat(locale, {
        style: 'long',
        type: 'conjunction'
    }).format(moments);

    return `${formattedMoments.charAt(0).toLocaleUpperCase(locale)}${formattedMoments.slice(1)}`;
};

const MedicationOverviewCard = ({ patientUuid, onViewMedication }) => {
    const { t, i18n } = useTranslation();
    const { data, isPending, isError, refetch } = useQuery({
        queryKey: ['patient-medications', patientUuid, 'active'],
        queryFn: () => getPatientMedications(patientUuid, true, false),
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const medications = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const mostRecentStart = useMemo(() => {
        const validDates = medications
            .map((medication) => dayjs(medication.start_ts))
            .filter((date) => date.isValid());

        if (validDates.length === 0) return null;

        return validDates.reduce((latest, date) =>
            date.isAfter(latest) ? date : latest
        );
    }, [medications]);

    return (
        <OverviewCard
            title={t('userPage.sections.medication')}
            icon={<MedicationOutlined fontSize="small" />}
            minHeight={230}
            contentInset={4}
        >
            {isPending ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                    <Skeleton width={150} height={24} />
                    {[1, 2, 3].map((item) => (
                        <Skeleton key={item} height={30} />
                    ))}
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.medications.loadError')}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => refetch()}>
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : (
                <>
                    <Typography
                        sx={{
                            mb: 0.75,
                            color: 'primary.main',
                            fontSize: '0.875rem',
                            fontWeight: 700
                        }}
                    >
                        {t('userPage.overview.medications.active', {
                            count: medications.length
                        })}
                    </Typography>

                    {medications.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {t('userPage.overview.medications.none')}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: 112,
                                overflowY: 'auto',
                                pr: 0.75,
                                scrollbarColor: '#B8C3CC transparent',
                                scrollbarWidth: 'thin',
                                '&::-webkit-scrollbar': { width: 5 },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: '#B8C3CC',
                                    borderRadius: 1
                                }
                            }}
                        >
                            {medications.map((medication, index) => (
                                <Box
                                    key={medication.uuid}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'minmax(180px, 0.8fr) minmax(0, 1.2fr)',
                                        alignItems: 'center',
                                        gap: 2,
                                        minHeight: 36,
                                        py: 0.5,
                                        borderTop: index === 0 ? 0 : '1px solid #E0E5E9'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        noWrap
                                        title={medication.name}
                                    >
                                        {medication.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        textAlign="right"
                                        noWrap
                                        title={formatTakingMoments(
                                            medication.dosage,
                                            t,
                                            i18n.resolvedLanguage
                                        )}
                                    >
                                        {formatTakingMoments(
                                            medication.dosage,
                                            t,
                                            i18n.resolvedLanguage
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 'auto',
                            mx: -4,
                            pt: 1
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {mostRecentStart
                                ? t('userPage.overview.medications.latestStart', {
                                      date: mostRecentStart.format('DD/MM/YYYY')
                                  })
                                : ''}
                        </Typography>
                        <Button
                            size="small"
                            endIcon={<ArrowForwardIcon fontSize="small" />}
                            onClick={onViewMedication}
                            sx={{ flexShrink: 0, px: 0.5, fontWeight: 600 }}
                        >
                            {t('userPage.overview.viewMedication')}
                        </Button>
                    </Box>
                </>
            )}
        </OverviewCard>
    );
};

MedicationOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onViewMedication: PropTypes.func.isRequired
};

export default MedicationOverviewCard;
